import { jsx, jsxs } from 'react/jsx-runtime';
import { useState } from 'react';
import { twMerge } from '@belong/common/twMerge';
import { IconCaretDown } from '@belong/icons/dist/IconCaretDown';
import { IconCheck2 } from '@belong/icons/dist/IconCheck2';
import { ListboxInput, ListboxButton, ListboxPopover, ListboxOption, ListboxList } from '@reach/listbox';
import clsx from 'clsx';
import { Form } from '../Form/Form.js';
import { Text } from '../Text/Text.js';
import css from './Dropdown.module.css.js';

function Dropdown({
  name,
  value,
  options,
  label,
  onChange,
  focused,
  error,
  border = true,
  limitPopOverHeight,
  errorMessageVisible = false,
  className,
  ...rest
}) {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const currentOption = options.find(option => option.value === value);
  const currentLabel = currentOption?.label;
  const isFocused = focused || isInputFocused;
  function onFocus(event) {
    if (rest.onFocus) {
      rest.onFocus(event);
    }
    setIsInputFocused(true);
  }
  function onBlur(event) {
    if (rest.onBlur) {
      rest.onBlur(event);
    }
    setIsInputFocused(false);
  }
  return jsx(ListboxInput, {
    ...rest,
    name: name,
    value: value,
    onChange: onChange,
    onBlur: onBlur,
    onFocus: onFocus,
    className: clsx(css.dropdownContainer, className),
    children: ({
      isExpanded
    }) => {
      return jsxs("div", {
        className: "flex-col",
        children: [jsxs("div", {
          className: twMerge(clsx('relative flex w-full items-end border-0 border-b-2 border-solid border-gray', {
            'h-labeled-fields': !!label,
            [`${css.customRounded}`]: !border,
            'border-b-0': !border,
            'border-red': !!error,
            'border-navy': !!value && !error,
            'border-green': (isExpanded || focused) && !error
          })),
          children: [!!label && jsx("label", {
            className: twMerge(clsx('pointer-events-none absolute left-0', {
              'text-red': !!error
            }, {
              'top-0 font-semibold text-navy': value
            }, {
              'top-0 font-semibold text-green': isExpanded || isFocused
            }, css.inputLabel)),
            htmlFor: name,
            children: label
          }), jsxs(ListboxButton, {
            children: [jsx(Text, {
              fontWeight: border ? 'regular' : 'semibold',
              className: "min-w-0 truncate",
              children: currentLabel
            }), jsx(IconCaretDown, {
              width: 15,
              className: clsx('ml-sm transition-transform duration-hover', {
                'rotate-180': isExpanded
              })
            })]
          }), jsxs(ListboxPopover, {
            portal: false,
            className: clsx({
              'overflow-y-scroll	max-h-[250px]': limitPopOverHeight
            }),
            style: limitPopOverHeight ? {
              maxHeight: '250px'
            } : {},
            children: [jsx(ListboxOption, {
              value: "default",
              className: "hidden"
            }), jsx(ListboxList, {
              children: options.map(option => {
                return jsx(ListboxOption, {
                  value: option.value,
                  children: jsxs("div", {
                    className: "flex justify-between",
                    children: [jsx(Text, {
                      fontWeight: value === option.value ? 'semibold' : 'regular',
                      children: option.label
                    }), value === option.value && jsx(IconCheck2, {
                      width: 12,
                      height: 12,
                      className: "shrink-0"
                    })]
                  })
                }, option.value);
              })
            })]
          })]
        }), errorMessageVisible && error && jsx(Form.ErrorMessage, {
          message: error
        })]
      });
    }
  });
}

export { Dropdown };
